// Staff chat MVP (SR-14): the calls to Scrips.Notifications `/api/Notifications/staff-chat` (same host and ingress as the
// other Notifications calls in this app, `/api/Notifications/...` through the shared api-client: Bearer JWT,
// OrganizationId header). Plain functions; the hooks in use-staff-chat.ts wrap them in TanStack Query.
import { get, post } from '@/shared/api/api-client';
import type {
  CreateStaffChatThread,
  StaffChatMessage,
  StaffChatPage,
  StaffChatPerson,
  StaffChatThread,
  StaffChatUnread,
} from '@/staff-chat/types/staff-chat';

export const STAFF_CHAT_BASE = '/api/Notifications/staff-chat';
export const THREADS_PAGE_SIZE = 30;
export const MESSAGES_PAGE_SIZE = 50;

const enc = encodeURIComponent;

function withQuery(path: string, params: Record<string, string | number | null | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== null && v !== undefined && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

export async function fetchDirectory(signal?: AbortSignal): Promise<StaffChatPerson[]> {
  const res = await get<{ items?: StaffChatPerson[] } | undefined>(`${STAFF_CHAT_BASE}/directory`, { signal });
  return res?.items ?? [];
}

export async function fetchThreads(cursor?: string | null, signal?: AbortSignal): Promise<StaffChatPage<StaffChatThread>> {
  const res = await get<StaffChatPage<StaffChatThread> | undefined>(
    withQuery(`${STAFF_CHAT_BASE}/threads`, { take: THREADS_PAGE_SIZE, cursor }),
    { signal },
  );
  return { items: res?.items ?? [], nextCursor: res?.nextCursor ?? null };
}

export const fetchThread = (threadId: string, signal?: AbortSignal) =>
  get<StaffChatThread>(`${STAFF_CHAT_BASE}/threads/${enc(threadId)}`, { signal });

export const createThread = (body: CreateStaffChatThread) => post<StaffChatThread>(`${STAFF_CHAT_BASE}/threads`, body);

export async function fetchMessages(
  threadId: string,
  cursor?: string | null,
  signal?: AbortSignal,
): Promise<StaffChatPage<StaffChatMessage>> {
  const res = await get<StaffChatPage<StaffChatMessage> | undefined>(
    withQuery(`${STAFF_CHAT_BASE}/threads/${enc(threadId)}/messages`, { take: MESSAGES_PAGE_SIZE, cursor }),
    { signal },
  );
  return { items: res?.items ?? [], nextCursor: res?.nextCursor ?? null };
}

export const sendMessage = (threadId: string, body: string, clientMessageId: string) =>
  post<StaffChatMessage>(`${STAFF_CHAT_BASE}/threads/${enc(threadId)}/messages`, { body, clientMessageId });

export const markThreadRead = (threadId: string) => post<void>(`${STAFF_CHAT_BASE}/threads/${enc(threadId)}/read`, undefined);

export async function fetchUnread(signal?: AbortSignal): Promise<number> {
  const res = await get<StaffChatUnread | undefined>(`${STAFF_CHAT_BASE}/unread`, { signal });
  const total = res?.total;
  return typeof total === 'number' && Number.isFinite(total) && total > 0 ? total : 0;
}
